/**
 * Layer State Management Hooks
 * 
 * React hooks for interacting with the global layer state store.
 * Provides both individual layer management and global preset control.
 */

import { useCallback, useMemo } from 'react';
import { LayerId } from '../styles/styles-data/zIndex';
import { useLayerStore } from '../stores/layerStore';
import { 
  LayerStateHook, 
  GlobalLayerStateHook, 
  LayerState, 
  PresetId 
} from '../types/layerState';

/**
 * Hook for managing individual layer state
 * 
 * @param layerId - The ID of the layer to manage
 * @returns LayerStateHook with state and control methods
 * 
 * @example
 * ```tsx
 * const overlayLayer = useLayerState('overlays');
 * 
 * // Check state
 * if (overlayLayer.isActive) { ... }
 * 
 * // Control layer
 * overlayLayer.activate();
 * overlayLayer.hide();
 * ```
 */
export const useLayerState = (layerId: LayerId): LayerStateHook => {
  // Subscribe to the specific layer's state
  const state = useLayerStore((store) => store.layerActivation[layerId]);
  const setLayerState = useLayerStore((store) => store.setLayerState);
  const activateLayer = useLayerStore((store) => store.activateLayer);
  const deactivateLayer = useLayerStore((store) => store.deactivateLayer);
  const disableLayerInteraction = useLayerStore((store) => store.disableLayerInteraction);
  const hideLayer = useLayerStore((store) => store.hideLayer);
  const showLayer = useLayerStore((store) => store.showLayer);

  // Memoized computed properties
  const computedState = useMemo(() => ({
    isActive: state.rendered && state.interactive && state.displayed,
    isVisible: state.displayed,
    isInteractive: state.interactive,
    isRendered: state.rendered,
  }), [state.rendered, state.interactive, state.displayed]);

  // Memoized action callbacks
  const actions = useMemo(() => ({
    activate: () => activateLayer(layerId),
    deactivate: () => deactivateLayer(layerId),
    setState: (newState: Partial<LayerState>) => setLayerState(layerId, newState),
    disableInteraction: () => disableLayerInteraction(layerId),
    hide: () => hideLayer(layerId),
    show: () => showLayer(layerId),
  }), [
    layerId, 
    activateLayer, 
    deactivateLayer, 
    setLayerState, 
    disableLayerInteraction, 
    hideLayer, 
    showLayer
  ]);

  return {
    state,
    ...computedState,
    ...actions,
  };
};

/**
 * Hook for global layer state management and presets
 * 
 * @returns GlobalLayerStateHook with preset control and layer access
 * 
 * @example
 * ```tsx
 * const globalLayers = useGlobalLayerState();
 * 
 * // Apply presets
 * globalLayers.showOverlay();
 * globalLayers.showMobileMenu();
 * 
 * // Check current state
 * const isOverlayActive = globalLayers.getCurrentPreset() === 'OVERLAY_ACTIVE';
 * ```
 */
export const useGlobalLayerState = (): GlobalLayerStateHook => {
  const currentPreset = useLayerStore((store) => store.currentPreset);
  const applyPreset = useLayerStore((store) => store.applyPreset);
  const getLayerState = useLayerStore((store) => store.getLayerState);
  const setLayerState = useLayerStore((store) => store.setLayerState);

  // Memoized convenience methods for common presets
  const presetActions = useMemo(() => ({
    showDefault: () => applyPreset('DEFAULT'),
    showOverlay: () => applyPreset('OVERLAY_ACTIVE'),
    showMobileMenu: () => applyPreset('MOBILE_MENU_ACTIVE'),
    showLoading: () => applyPreset('LOADING'),
  }), [applyPreset]);

  // Memoized utility methods
  const utilities = useMemo(() => ({
    getCurrentPreset: (): PresetId => currentPreset,
    getLayer: (layerId: LayerId): LayerState => getLayerState(layerId),
    setLayer: (layerId: LayerId, state: Partial<LayerState>) => setLayerState(layerId, state),
  }), [currentPreset, getLayerState, setLayerState]);

  return {
    currentPreset,
    applyPreset,
    ...presetActions,
    ...utilities,
  };
};

/**
 * Hook for layer-specific event handlers with automatic state management
 * 
 * @param layerId - The layer to manage
 * @returns Object with common event handlers
 * 
 * @example
 * ```tsx
 * const overlayHandlers = useLayerHandlers('overlays');
 * 
 * <div 
 *   onClick={overlayHandlers.onActivate}
 *   onClose={overlayHandlers.onDeactivate}
 * >
 * ```
 */
export const useLayerHandlers = (layerId: LayerId) => {
  const { activate, deactivate, hide, show } = useLayerState(layerId);

  return useMemo(() => ({
    onActivate: activate,
    onDeactivate: deactivate,
    onShow: show,
    onHide: hide,
    onToggle: () => {
      const state = useLayerStore.getState().getLayerState(layerId);
      if (state.displayed) {
        hide();
      } else {
        show();
      }
    },
  }), [activate, deactivate, hide, show, layerId]);
};

/**
 * Hook for debugging layer states in development
 * 
 * @returns Debug utilities
 */
export const useLayerDebug = () => {
  const layerActivation = useLayerStore((store) => store.layerActivation);
  const currentPreset = useLayerStore((store) => store.currentPreset);

  return useMemo(() => ({
    layerActivation,
    currentPreset,
    logCurrentState: () => {
      if (process.env.NODE_ENV === 'development') {
        console.group(`[LayerDebug] Current Preset: ${currentPreset}`);
        Object.entries(layerActivation).forEach(([layerId, state]) => {
          console.log(`${layerId}:`, state);
        });
        console.groupEnd();
      }
    },
    getLayerStatus: (layerId: LayerId) => {
      const state = layerActivation[layerId];
      if (!state) return 'UNKNOWN';
      
      if (state.rendered && state.interactive && state.displayed) return 'ACTIVE';
      if (state.rendered && state.displayed) return 'VISIBLE';
      if (state.rendered) return 'MOUNTED';
      return 'INACTIVE';
    },
  }), [layerActivation, currentPreset]);
};

/**
 * Hook for layer transitions with animation support
 * 
 * @param layerId - The layer to manage transitions for
 * @param transitionDuration - Duration in ms for transitions
 * @returns Transition control methods
 */
export const useLayerTransition = (layerId: LayerId, transitionDuration = 300) => {
  const { state, setState } = useLayerState(layerId);

  const transitionTo = useCallback(async (targetState: Partial<LayerState>) => {
    // If hiding, fade out first then unmount
    if (targetState.displayed === false && state.displayed) {
      // Apply CSS transition class or handle fade out
      await new Promise(resolve => setTimeout(resolve, transitionDuration));
      setState(targetState);
    }
    // If showing, mount first then fade in
    else if (targetState.displayed === true && !state.displayed) {
      setState({ ...targetState, displayed: false }); // Mount but hidden
      await new Promise(resolve => setTimeout(resolve, 50)); // Allow DOM update
      setState({ displayed: true }); // Trigger fade in
    }
    // Immediate state change for other transitions
    else {
      setState(targetState);
    }
  }, [state, setState, transitionDuration]);

  const fadeIn = useCallback(() => {
    transitionTo({ rendered: true, displayed: true, interactive: true });
  }, [transitionTo]);

  const fadeOut = useCallback(() => {
    transitionTo({ displayed: false, interactive: false });
  }, [transitionTo]);

  return {
    transitionTo,
    fadeIn,
    fadeOut,
    isTransitioning: false, // Could be enhanced to track transition state
  };
};